import {
  createContext,
  useEffect,
  useState
} from 'react';

import {
  useLocation
} from 'react-router-dom';

import {
  JourneyProvider
} from '@vodacom/web-common';

import
  Bowser
from 'bowser';

import {
  AAPromptStates,
  CacheKeys,
  ConfigContexts,
  OriginContexts
} from '../constants';

import {
  CommonProvider,
  DrupalProvider
} from '../providers';

import {
  HandleRender
} from 'hocs';

import {
  hasInsufficientBalance,
  isEqual
} from 'helpers';

export const AppContext = createContext();

export const AppProvider = ({ children }) => {

  const { pathname, search } = useLocation();
  const context = JourneyProvider.get(CacheKeys.ConfigContext, 'value', new URLSearchParams(search).get('context'));

  const device = Bowser.getParser(window.navigator.userAgent);
  const [ appState, setAppState ] = useState({
    device: device,
    isIOS: device.getOSName(true) === 'ios',
    isANDROID: device.getOSName(true) === 'android',
    config: null,
    isTransitioning: false,
    isLoading: true,
    bundleBalances: null,
    showAirtimeAdvancePrompt: false,
    promptState: JourneyProvider.get(CacheKeys.AdvancePrompt, 'state', AAPromptStates.Idle),
    setPromptState: (newState) => {

      JourneyProvider.set(CacheKeys.AdvancePrompt, { state: newState });

      setAppState(prevState => ({
        ...prevState,
        promptState: newState
      }));
    }
  });

  const initConfig = async () => {

    const configContext = isEqual(context, OriginContexts.VodaPay) ? ConfigContexts.VodaPay : ConfigContexts.ConnectU;

    const cachedConfig = await DrupalProvider.getCachedItem(CacheKeys.Config, null, () => DrupalProvider.getConfig(configContext));
    const config = cachedConfig?.result?.[0];

    if (cachedConfig?.ok) {

      setAppState(prevState => ({
        ...prevState,
        config: config,
        isLoading: false
      }));

      return config;
    }

    const defaultConfig = require('./default-config.json');

    setAppState(prevState => ({
      ...prevState,
      config: defaultConfig,
      isLoading: false
    }));

    return defaultConfig;
  };

  const setTransitionStarted = () => {
    setAppState(prevState => ({
      ...prevState,
      isTransitioning: true
    }));

    const { googletag } = window;
    googletag.cmd.push(() => {
      googletag.destroySlots();
    });
  };

  const setTransitionComplete = () => {
    setAppState(prevState => ({
      ...prevState,
      isTransitioning: false
    }));
  };

  const handleTransition = () => {

    if (!/^\/advance/.test(pathname) && appState.promptState === AAPromptStates.Started) {

      JourneyProvider.set('Prompt', { url: '' });
      appState.setPromptState(AAPromptStates.Idle);

      return;
    }
  };

  const getBundleBalances = async () => {

    if (appState.bundleBalances) {
      return;
    }

    const res = await CommonProvider.getBundleBalances();

    if (!res?.ok || !res?.result) {
      return;
    }

    setAppState(prevState => ({
      ...prevState,
      bundleBalances: res?.result,
      showAirtimeAdvancePrompt: hasInsufficientBalance(
        res?.result?.dataBalance,
        res?.result?.airtimeBalance,
        appState.config?.advancePromptConfig?.minimumDataBalance,
        appState.config?.advancePromptConfig?.minimumAirtimeBalance
      )
    }));
  };

  useEffect(() => {

    JourneyProvider.set(CacheKeys.ConfigContext, { value: context }, false);

    const init = async () => {

      await initConfig();

      // TODO is this even needed after sentry removal?
      const initMiniAppMessaging = () => {

        if (!window?.my) {
          return;
        }

        window.my.postMessage({ name: 'pwa-ready' });
      };

      if (isEqual(context, OriginContexts.VodaPay)) {
        initMiniAppMessaging();
      }
    };

    init();

  }, []);

  useEffect(() => {

    setTransitionStarted();

    handleTransition();

    setTransitionComplete();

    getBundleBalances();
  }, [ pathname ]);

  return (

    <HandleRender
      isError={false}
      isLoading={appState.isLoading}
      loader={{ height: '100vh' }}>

      <AppContext.Provider value={{
        ...appState,
        setAppState
      }}>
        { children }
      </AppContext.Provider>
    </HandleRender>
  );
};
